import { lazy, Suspense, useState } from "react";
import { HERO_IMG, PLAY_BTN, quickQuoteUrl, heroVideoUrl } from "@/lib/site-core";
import { useHairLossSwap } from "@/lib/hair-loss-copy";

const VideoDialog = lazy(() =>
  import("@/components/site/VideoDialog").then((m) => ({ default: m.VideoDialog })),
);

const DEFAULT_SUBHEADING =
  "Restore your confidence with the top hair restoration team trusted by celebrities, athletes & everyday guys.";

type HeroProps = {
  eyebrow?: string;
  // H1 text. If not set, the page uses the default H1 (with the route swap).
  heading?: string;
  // Line under the H1. If not set, the page uses the default line.
  subheading?: string;
};

export function Hero({
  eyebrow = "RESTORE® Hair | Hair Transplants",
  heading,
  subheading = DEFAULT_SUBHEADING,
}: HeroProps) {
  const [open, setOpen] = useState(false);
  const swap = useHairLossSwap();
  return (
    <section data-cta-location="hero" className="bg-background">
      <div className="mx-auto grid max-w-7xl items-end gap-10 px-4 pt-4 md:pt-6 lg:grid-cols-2 lg:px-8 lg:pt-8">
        <div className="pb-8 md:pb-10 lg:pb-12">
          <p className="text-sm font-bold tracking-wide text-foreground">
            {swap(eyebrow)}
          </p>
          <h1 className="mt-3 text-5xl font-extrabold leading-tight text-foreground md:text-6xl">
            {heading ?? swap("Get Your Hair Back")}
          </h1>
          <p className="mt-5 text-xl font-medium text-foreground md:text-2xl">
            {subheading}
          </p>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            From celebrities and athletes to businessmen and everyman, RESTORE® Hair has performed
            over 20,000 hair restoration procedures — but you won't recognize our work. That's a
            good thing because the results are soft, natural, and undetectable.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            No hair plugs, no staples, no linear scarring — just results.
          </p>
          <a
            href={quickQuoteUrl}
            className="mt-8 inline-block bg-primary px-8 py-4 text-sm font-bold tracking-wider text-primary-foreground transition hover:bg-primary/90"
          >
            Get a Quick Quote
          </a>
        </div>
        <div className="relative self-end">
          {/* Never wider than the 507px file, so it is not blown up and soft on tablets.
              Name labels: above the photo on phones, over its empty top on md+. Each has
              half the photo width, so they no longer run into each other (they did at ~1024px). */}
          <div className="relative mx-auto max-w-[507px]">
            <div className="grid grid-cols-2 gap-3 pb-3 md:absolute md:inset-x-0 md:top-0 md:z-10 md:pb-0">
              <div className="min-w-0 text-xs font-medium text-foreground">
                <span className="inline-block bg-[#F7E6BF] px-2 py-1 text-xs font-medium text-foreground lg:text-sm">
                  Hall of Fame Football Player
                </span>
                <div className="mt-1 text-base font-bold lg:text-lg">Brian Urlacher</div>
                <div className="text-xs italic text-muted-foreground">RESTORE Client</div>
              </div>
              <div className="min-w-0 text-right text-xs font-medium text-foreground">
                <span className="inline-block bg-[#F7E6BF] px-2 py-1 text-xs font-medium text-foreground lg:text-sm">
                  Hall of Fame Football Player
                </span>
                <div className="mt-1 text-base font-bold lg:text-lg">Deion Sanders</div>
                <div className="text-xs italic text-muted-foreground">RESTORE Client</div>
              </div>
            </div>
            <img
              src={HERO_IMG}
              srcSet={`/img/hero-380.webp 380w, ${HERO_IMG} 507w`}
              sizes="(max-width: 768px) 100vw, 507px"
              alt="Brian Urlacher and Deion Sanders"
              width={507}
              height={447}
              fetchPriority="high"
              decoding="async"
              className="block w-full h-auto"
            />
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Play video"
              className="absolute left-1/2 top-[78%] md:top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              <img decoding="async" src={PLAY_BTN} alt="" width={184} height={184} className="h-16 w-16 md:h-20 md:w-20" />
            </button>
          </div>
        </div>
      </div>
      {open && (
        <Suspense fallback={null}>
          <VideoDialog open={open} onOpenChange={setOpen} src={heroVideoUrl} />
        </Suspense>
      )}
    </section>
  );
}